import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  updateLocation,
  getLocationById,
  getLocationSectionConfig,
  updateLocationSectionOverrides,
  getPlatformSectionVisibility,
  useQuery,
} from "wasp/client/operations";
import { type Location } from "wasp/entities";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ArrowLeft, MapPin, Eye } from "lucide-react";
import Breadcrumb from "../../admin/layout/Breadcrumb";
import DefaultLayout from "../../admin/layout/DefaultLayout";
import LoadingSpinner from "../../admin/layout/LoadingSpinner";
import { SIDEBAR_SECTION_KEYS } from "../location-types/constants";
import { SECTION_LABELS } from "../location-types/constants";

const EditLocationPage = () => {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [sectionOverridesSaving, setSectionOverridesSaving] = useState(false);
  const [sectionOverrideForm, setSectionOverrideForm] = useState<
    Record<string, boolean | null>
  >({});
  const [formData, setFormData] = useState({
    name: "",
    type: "OTHER",
    isPublic: false,
    allowPublicSubscription: true,
    defaultVisibility: "VERIFIED",
    publicDescription: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    timezone: "UTC",
    latitude: "",
    longitude: "",
  });

  // Get location data
  const { data: location, isLoading: loadingLocation } = useQuery(
    getLocationById,
    { id: id! },
    { enabled: !!id },
  );

  // Get section config for this location (for override form)
  const { data: sectionConfig } = useQuery(
    getLocationSectionConfig,
    { locationId: id! },
    { enabled: !!id },
  );
  const { data: platformVisibility } = useQuery(getPlatformSectionVisibility);
  const platformByKey =
    (platformVisibility as { byKey?: Record<string, boolean> } | undefined)
      ?.byKey ?? {};
  /** Only sections that are globally visible can be overridden per location (cannot override Section Visibility). */
  const visibleSectionKeys = SIDEBAR_SECTION_KEYS.filter(
    (k) => platformByKey[k] !== false,
  );

  const sectionConfigTyped = sectionConfig as
    | {
        overrideByKey?: Record<string, boolean | null>;
        typeDefaults?: Record<string, boolean>;
      }
    | undefined;
  useEffect(() => {
    if (sectionConfigTyped?.overrideByKey) {
      const next: Record<string, boolean | null> = {};
      for (const key of SIDEBAR_SECTION_KEYS) {
        next[key] = sectionConfigTyped.overrideByKey![key] ?? null;
      }
      setSectionOverrideForm(next);
    }
  }, [sectionConfigTyped?.overrideByKey]);

  useEffect(() => {
    if (location) {
      const loc = location as Location;
      setFormData({
        name: loc.name || "",
        type: loc.type || "OTHER",
        isPublic: loc.isPublic || false,
        allowPublicSubscription: loc.allowPublicSubscription ?? true,
        defaultVisibility: loc.defaultVisibility || "VERIFIED",
        publicDescription: loc.publicDescription || "",
        address: loc.address || "",
        city: loc.city || "",
        state: loc.state || "",
        country: loc.country || "",
        postalCode: loc.postalCode || "",
        timezone: loc.timezone || "UTC",
        latitude: loc.latitude?.toString() || "",
        longitude: loc.longitude?.toString() || "",
      });
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsLoading(true);

    try {
      const locationData = {
        id,
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude
          ? parseFloat(formData.longitude)
          : undefined,
      };

      await updateLocation(locationData);
      navigate("/admin/locations");
    } catch (error) {
      alert("Failed to update location: " + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSectionOverrideChange = (
    key: string,
    value: "default" | "show" | "hide",
  ) => {
    setSectionOverrideForm((prev) => ({
      ...prev,
      [key]: value === "default" ? null : value === "show",
    }));
  };

  /** Effective visibility: override if set, else type default */
  const getEffectiveShow = (key: string) => {
    const override = sectionOverrideForm[key];
    if (override !== null && override !== undefined) return override;
    return sectionConfigTyped?.typeDefaults?.[key] ?? true;
  };

  const handleSaveSectionOverrides = async () => {
    if (!id) return;
    setSectionOverridesSaving(true);
    try {
      const payload: Record<string, boolean | null> = {};
      for (const key of SIDEBAR_SECTION_KEYS) {
        const v = sectionOverrideForm[key];
        payload[key] = v === undefined ? null : v;
      }
      await updateLocationSectionOverrides({ locationId: id, ...payload });
      alert("Section visibility overrides saved.");
    } catch (e: any) {
      alert("Failed to save: " + (e?.message || "Unknown error"));
    } finally {
      setSectionOverridesSaving(false);
    }
  };

  if (!user?.isAdmin) {
    return <div>Access denied. Admin privileges required.</div>;
  }

  if (loadingLocation) {
    return (
      <DefaultLayout user={user}>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </DefaultLayout>
    );
  }

  if (!location) {
    return (
      <DefaultLayout user={user}>
        <div className="text-center py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Location Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The location you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/admin/locations")}>
            Back to Locations
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        <Breadcrumb pageName="Edit Location" />

        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/locations")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Locations
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Edit Location
              </h1>
              <p className="text-gray-600">Update location information</p>
            </div>
          </div>

          {/* Location Code Display */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gray-500" />
                <span className="text-sm text-gray-600">Location Code:</span>
                <span className="font-mono text-sm font-medium">
                  {(location as Location)?.locationCode}
                </span>
              </div>
            </CardContent>
          </Card>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Location Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Enter location name"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="type">Location Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) =>
                        handleInputChange("type", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="APARTMENT_BUILDING">
                          Apartment Building
                        </SelectItem>
                        <SelectItem value="INNOVATION_HUB">
                          Innovation Hub
                        </SelectItem>
                        <SelectItem value="COWORKING_SPACE">
                          Coworking Space
                        </SelectItem>
                        <SelectItem value="SHOPPING_CENTER">
                          Shopping Center
                        </SelectItem>
                        <SelectItem value="CAMPUS">Campus</SelectItem>
                        <SelectItem value="COMMUNITY_CENTER">
                          Community Center
                        </SelectItem>
                        <SelectItem value="MIXED_USE">Mixed Use</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="publicDescription">Public Description</Label>
                  <Textarea
                    id="publicDescription"
                    value={formData.publicDescription}
                    onChange={(e) =>
                      handleInputChange("publicDescription", e.target.value)
                    }
                    placeholder="Describe this location for public visitors"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Location Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Location Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label htmlFor="isPublic">Public Location</Label>
                    <p className="text-sm text-gray-600">
                      Allow public visitors to view this location
                    </p>
                  </div>
                  <div
                    role="group"
                    aria-label="Public location: yes or no"
                    className="inline-flex rounded-lg border-2 border-input bg-muted/50 p-1 gap-0.5"
                  >
                    <button
                      type="button"
                      id="isPublic"
                      onClick={() => handleInputChange("isPublic", true)}
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                        formData.isPublic
                          ? "bg-primary text-white shadow-sm border border-primary"
                          : "text-muted-foreground hover:bg-muted border border-transparent"
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInputChange("isPublic", false)}
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                        !formData.isPublic
                          ? "bg-primary text-white shadow-sm border border-primary"
                          : "text-muted-foreground hover:bg-muted border border-transparent"
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label htmlFor="allowPublicSubscription">
                      Allow Public Subscription
                    </Label>
                    <p className="text-sm text-gray-600">
                      Allow users to subscribe without invitation
                    </p>
                  </div>
                  <div
                    role="group"
                    aria-label="Allow public subscription: yes or no"
                    className="inline-flex rounded-lg border-2 border-input bg-muted/50 p-1 gap-0.5"
                  >
                    <button
                      type="button"
                      id="allowPublicSubscription"
                      onClick={() =>
                        handleInputChange("allowPublicSubscription", true)
                      }
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                        formData.allowPublicSubscription
                          ? "bg-primary text-white shadow-sm border border-primary"
                          : "text-muted-foreground hover:bg-muted border border-transparent"
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleInputChange("allowPublicSubscription", false)
                      }
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                        !formData.allowPublicSubscription
                          ? "bg-primary text-white shadow-sm border border-primary"
                          : "text-muted-foreground hover:bg-muted border border-transparent"
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="defaultVisibility">
                    Default Content Visibility
                  </Label>
                  <Select
                    value={formData.defaultVisibility}
                    onValueChange={(value) =>
                      handleInputChange("defaultVisibility", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PUBLIC">Public</SelectItem>
                      <SelectItem value="SUBSCRIBER">
                        Subscribers Only
                      </SelectItem>
                      <SelectItem value="VERIFIED">
                        Verified Members Only
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Section visibility overrides (per-location / per-hub) - placed high so it's easy to find */}
            {id && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Section visibility
                  </CardTitle>
                  <CardDescription>
                    Override section visibility for this location (overrides
                    Location Type only; global Section Visibility cannot be
                    overridden). Only sections visible globally are listed. Use
                    default to follow the location type.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {visibleSectionKeys.length === 0 ? (
                      <p className="text-sm text-gray-500 col-span-full">
                        No sections are visible globally. Enable sections in
                        Platform section visibility first.
                      </p>
                    ) : (
                      visibleSectionKeys.map((key) => {
                        const typeDefault =
                          sectionConfigTyped?.typeDefaults?.[key] ?? true;
                        const effectiveShow = getEffectiveShow(key);
                        const label = SECTION_LABELS[key] ?? key;
                        return (
                          <div
                            key={key}
                            className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0"
                          >
                            <Label className="text-sm font-medium shrink-0">
                              {label}
                              <span className="ml-1 font-normal text-muted-foreground">
                                (Default: {typeDefault ? "Show" : "Hide"})
                              </span>
                            </Label>
                            <div
                              role="group"
                              aria-label={`${label}: show or hide`}
                              className="inline-flex rounded-lg border-2 border-input bg-muted/50 p-1 gap-0.5"
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  handleSectionOverrideChange(key, "show")
                                }
                                className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                                  effectiveShow
                                    ? "bg-primary text-white shadow-sm border border-primary"
                                    : "text-muted-foreground hover:bg-muted border border-transparent"
                                }`}
                              >
                                Show
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleSectionOverrideChange(key, "hide")
                                }
                                className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
                                  !effectiveShow
                                    ? "bg-primary text-white shadow-sm border border-primary"
                                    : "text-muted-foreground hover:bg-muted border border-transparent"
                                }`}
                              >
                                Hide
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleSaveSectionOverrides}
                    disabled={sectionOverridesSaving}
                  >
                    {sectionOverridesSaving
                      ? "Saving..."
                      : "Save section overrides"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Address Information */}
            <Card>
              <CardHeader>
                <CardTitle>Address Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      placeholder="City"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="state">State/Province *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) =>
                        handleInputChange("state", e.target.value)
                      }
                      placeholder="State/Province"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) =>
                        handleInputChange("country", e.target.value)
                      }
                      placeholder="Country"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postalCode">Postal Code</Label>
                    <Input
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={(e) =>
                        handleInputChange("postalCode", e.target.value)
                      }
                      placeholder="12345"
                    />
                  </div>

                  <div>
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select
                      value={formData.timezone}
                      onValueChange={(value) =>
                        handleInputChange("timezone", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UTC">UTC</SelectItem>
                        <SelectItem value="America/New_York">
                          Eastern Time
                        </SelectItem>
                        <SelectItem value="America/Chicago">
                          Central Time
                        </SelectItem>
                        <SelectItem value="America/Denver">
                          Mountain Time
                        </SelectItem>
                        <SelectItem value="America/Los_Angeles">
                          Pacific Time
                        </SelectItem>
                        <SelectItem value="Europe/London">London</SelectItem>
                        <SelectItem value="Europe/Paris">Paris</SelectItem>
                        <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                        <SelectItem value="Asia/Shanghai">Shanghai</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Coordinates */}
            <Card>
              <CardHeader>
                <CardTitle>Coordinates (Optional)</CardTitle>
                <p className="text-sm text-gray-600">
                  Used for generating unique location codes and mapping
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) =>
                        handleInputChange("latitude", e.target.value)
                      }
                      placeholder="40.7128"
                    />
                  </div>

                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) =>
                        handleInputChange("longitude", e.target.value)
                      }
                      placeholder="-74.0060"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/locations")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Location"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default EditLocationPage;
